import { priceLine, totalDiscountFromBps } from './finance.js';
import {
  applyDraftPatch,
  canonicalJson,
  cloneJson,
  type DraftPatchChange,
  type DraftPatchOp,
} from './draft-patch.js';
import type { PosCartV1 } from './screen-context.js';

/**
 * "FACA POR MIM" NO PDV/BALCAO (A4) - REGRAS PURAS DO PATCH `pos.cart.v1`.
 *
 * Tudo aqui e funcao pura, usada IGUAL pelo servidor (que gera e valida o
 * patch) e pela pagina (que o valida de novo antes de aplicar):
 *
 * - quais caminhos do rascunho um patch pode tocar (lista permitida);
 * - como uma intencao ("cartao em 10x", "5% na camiseta") vira operacoes, ja
 *   conferidas contra a alcada de desconto, as regras de parcelas e o saldo;
 * - o diff por campo e o desfazer por campo.
 *
 * O patch SO muda o rascunho. Confirmar a venda e sempre o botao da pessoa.
 */

export type PosPaymentMethod = 'cash' | 'pix' | 'card';

/* -------------------------------------------------------------------------- */
/* Lista permitida de caminhos                                                 */
/* -------------------------------------------------------------------------- */

/** Nunca por patch: `location`, `productId`/`variantId` de linha existente, `accountId`, `creditId`. */
const ALLOWED_PATHS: RegExp[] = [
  /^\/customerRef$/,
  /^\/displayedTotalCents$/,
  /^\/lines\/(0|[1-9]\d*)$/,
  /^\/lines\/-$/,
  /^\/lines\/(0|[1-9]\d*)\/(quantity|discountBps)$/,
  /^\/payments\/-$/,
  /^\/payments\/(0|[1-9]\d*)$/,
  /^\/payments\/(0|[1-9]\d*)\/(method|amountCents|tenderedCents|installments|cardMethodId|received)$/,
];

/** `null` se a operacao pode entrar; senao o caminho que a recusa. */
export function posPatchOpViolation(op: DraftPatchOp): string | null {
  if (!ALLOWED_PATHS.some((pattern) => pattern.test(op.path))) return op.path;
  // O atestado "conferi o recebimento" e da pessoa: o Rimati so pode retira-lo.
  if (/\/received$/.test(op.path) && !(op.op === 'replace' && op.value === false)) return op.path;
  return null;
}

/** Primeiro caminho fora da lista permitida, ou `null`. */
export function posPatchViolation(ops: readonly DraftPatchOp[]): string | null {
  for (const op of ops) {
    const violation = posPatchOpViolation(op);
    if (violation) return violation;
  }
  return null;
}

/* -------------------------------------------------------------------------- */
/* Rotulos e valores legiveis                                                  */
/* -------------------------------------------------------------------------- */

const money = (cents: number): string =>
  (cents / 100)
    .toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    .replace(/\u00a0/g, ' ');

const METHOD_LABEL: Record<string, string> = {
  cash: 'Dinheiro',
  pix: 'Pix',
  card: 'Cartão',
  credit: 'Crédito da loja',
};

/**
 * Campo de pessoa -> rotulo da tela. `lines.<variantId>.x` e `payments.<n>.x`.
 * O rotulo e o da tela (catalogo de alvos), nunca texto do modelo.
 */
export function posFieldLabel(field: string, lineName?: (variantId: string) => string): string {
  const line = /^lines\.([^.]+)(?:\.(quantity|discountBps))?$/.exec(field);
  if (line) {
    const name = lineName?.(line[1]!) ?? 'Item';
    if (line[2] === 'quantity') return `Quantidade (${name})`;
    if (line[2] === 'discountBps') return `Desconto (${name})`;
    return name;
  }
  if (field === 'customerRef') return 'Cliente';
  const payment = /^payments\.(\d+)(?:\.(\w+))?$/.exec(field);
  if (payment) {
    const n = Number(payment[1]) + 1;
    const suffix = n > 1 ? ` ${n}` : '';
    switch (payment[2]) {
      case 'method':
        return `Forma de pagamento${suffix}`;
      case 'installments':
        return `Parcelas${suffix}`;
      case 'amountCents':
        return `Valor do pagamento${suffix}`;
      case 'tenderedCents':
        return `Dinheiro entregue${suffix}`;
      case 'cardMethodId':
        return `Cartão${suffix}`;
      case 'received':
        return `Recebimento conferido${suffix}`;
      default:
        return `Pagamento${suffix}`;
    }
  }
  return field;
}

export function posFormatValue(field: string, value: unknown): string {
  if (value === undefined || value === null) return '(vazio)';
  if (/\.method$/.test(field)) return METHOD_LABEL[String(value)] ?? String(value);
  if (/\.(amountCents|tenderedCents)$/.test(field)) return money(Number(value));
  if (/\.installments$/.test(field)) return `${String(value)}x`;
  if (/\.discountBps$/.test(field)) return `${(Number(value) / 100).toLocaleString('pt-BR')}%`;
  if (/\.quantity$/.test(field)) return String(value);
  if (/\.received$/.test(field)) return value ? 'Sim' : 'Não';
  if (field === 'customerRef') return 'Cliente selecionado';
  if (typeof value === 'object') {
    const line = value as { quantity?: number };
    return line.quantity ? `${line.quantity} un.` : 'Item';
  }
  return String(value);
}

/* -------------------------------------------------------------------------- */
/* Diff por campo, valor de campo e desfazer por campo                         */
/* -------------------------------------------------------------------------- */

type Line = PosCartV1['lines'][number];
type Payment = PosCartV1['payments'][number];

const PAYMENT_FIELDS = [
  'method',
  'amountCents',
  'tenderedCents',
  'installments',
  'cardMethodId',
  'received',
] as const;

/** Valor de um campo de pessoa no rascunho; `undefined` se nao existe. */
export function posFieldValue(cart: PosCartV1, field: string): unknown {
  if (field === 'customerRef') return cart.customerRef;
  const line = /^lines\.([^.]+)(?:\.(quantity|discountBps))?$/.exec(field);
  if (line) {
    const found = cart.lines.find((item) => item.variantId === line[1]);
    if (!found) return undefined;
    return line[2] ? found[line[2] as 'quantity' | 'discountBps'] : found;
  }
  const payment = /^payments\.(\d+)(?:\.(\w+))?$/.exec(field);
  if (payment) {
    const found = cart.payments[Number(payment[1])];
    if (!found) return undefined;
    return payment[2] ? found[payment[2] as keyof Payment] : found;
  }
  return undefined;
}

/**
 * O que mudou entre dois rascunhos, por campo. Linhas sao identificadas por
 * `variantId` (estavel), nunca por indice. O total exibido e derivado: fica de fora.
 */
export function diffPosCart(before: PosCartV1, after: PosCartV1): DraftPatchChange[] {
  const changes: DraftPatchChange[] = [];
  const push = (field: string, from: unknown, to: unknown): void => {
    if (canonicalJson(from) !== canonicalJson(to)) changes.push({ field, from, to });
  };
  push('customerRef', before.customerRef, after.customerRef);
  for (const line of after.lines) {
    const old = before.lines.find((item) => item.variantId === line.variantId);
    if (!old) push(`lines.${line.variantId}`, undefined, line);
    else {
      push(`lines.${line.variantId}.quantity`, old.quantity, line.quantity);
      push(`lines.${line.variantId}.discountBps`, old.discountBps, line.discountBps);
    }
  }
  for (const old of before.lines)
    if (!after.lines.some((item) => item.variantId === old.variantId))
      push(`lines.${old.variantId}`, old, undefined);
  const count = Math.max(before.payments.length, after.payments.length);
  for (let index = 0; index < count; index += 1) {
    const was = before.payments[index];
    const now = after.payments[index];
    if (!was || !now) push(`payments.${index}`, was, now);
    else for (const key of PAYMENT_FIELDS) push(`payments.${index}.${key}`, was[key], now[key]);
  }
  return changes;
}

/**
 * Desfaz UMA mudanca no rascunho de agora (FP1: desfazer por campo). So mexe no
 * campo se ele ainda tem o valor que o Rimati colocou; se a pessoa mudou depois,
 * devolve `null` e nada e desfeito ("Mantive Parcelas em 6, que voce alterou").
 */
export function revertPosChange(current: PosCartV1, change: DraftPatchChange): PosCartV1 | null {
  if (canonicalJson(posFieldValue(current, change.field)) !== canonicalJson(change.to))
    return null;
  const next = cloneJson(current);
  if (change.field === 'customerRef') {
    next.customerRef = change.from as string | null;
    return next;
  }
  const line = /^lines\.([^.]+)(?:\.(quantity|discountBps))?$/.exec(change.field);
  if (line) {
    const index = next.lines.findIndex((item) => item.variantId === line[1]);
    if (line[2]) {
      if (index < 0) return null;
      next.lines[index]![line[2] as 'quantity' | 'discountBps'] = change.from as number;
    } else if (change.from === undefined) {
      if (index >= 0) next.lines.splice(index, 1);
    } else if (index < 0) next.lines.push(change.from as Line);
    return next;
  }
  const payment = /^payments\.(\d+)(?:\.(\w+))?$/.exec(change.field);
  if (payment) {
    const index = Number(payment[1]);
    if (payment[2]) {
      const target = next.payments[index];
      if (!target) return null;
      (target as Record<string, unknown>)[payment[2]] = change.from;
    } else if (change.from === undefined) next.payments.splice(index, 1);
    else next.payments[index] = change.from as Payment;
    return next;
  }
  return null;
}

/**
 * DECISAO DO DONO PENDENTE (FP1): a granularidade do desfazer mora AQUI, numa
 * constante. 'field' = um botao Desfazer por campo (recomendado); 'all' = um
 * unico "Desfazer tudo". Trocar o valor e a unica mudanca necessaria.
 */
export const POS_UNDO_GRANULARITY: 'field' | 'all' = 'field';

const DERIVED_PAYMENT_FIELD = /^payments\.\d+\.(amountCents|tenderedCents|received)$/;

/** Planeja o desfazer: o rascunho resultante e o que foi mantido por ter sido editado. */
export function planPosUndo(
  current: PosCartV1,
  changes: readonly DraftPatchChange[],
  scope: 'field' | 'all',
  field?: string,
): { next: PosCartV1; undone: string[]; kept: string[] } {
  const targets = scope === 'all' || !field ? changes : changes.filter((c) => c.field === field);
  let next = current;
  const undone: string[] = [];
  const kept: string[] = [];
  // Do fim para o inicio: desfaz na ordem inversa da aplicacao.
  for (const change of [...targets].reverse()) {
    const reverted = revertPosChange(next, change);
    if (reverted) {
      next = reverted;
      undone.push(change.field);
    } else kept.push(change.field);
  }
  // Campos DERIVADOS: o valor do pagamento acompanha o total. Desfazer um item (quantidade,
  // desconto, linha) devolve tambem o valor/troco/conferencia que o Rimati derivou dele,
  // desde que a pessoa nao os tenha editado depois; assim o pagamento fica coerente com o total.
  if (undone.some((field) => field.startsWith('lines.'))) {
    for (const change of [...changes].reverse()) {
      if (!DERIVED_PAYMENT_FIELD.test(change.field) || undone.includes(change.field)) continue;
      const reverted = revertPosChange(next, change);
      if (reverted) {
        next = reverted;
        undone.push(change.field);
        const at = kept.indexOf(change.field);
        if (at >= 0) kept.splice(at, 1);
      } else if (!kept.includes(change.field)) kept.push(change.field);
    }
  }
  return { next, undone, kept };
}

/* -------------------------------------------------------------------------- */
/* Regras da loja que o patch respeita                                         */
/* -------------------------------------------------------------------------- */

export interface PosPatchRules {
  /** Alcada de desconto do papel, em pontos-base (`salesDiscountLimit`). */
  discountLimitBps: number;
  maxInstallments: number;
  minInstallmentCents: number;
  allowSplit: boolean;
  allowedMethods: ReadonlySet<PosPaymentMethod>;
  requireCustomer: boolean;
  /** Balcao e PDV operacional: nao escolhem cartao nem conta. */
  operational: boolean;
  /** Balcao: um unico desconto para o carrinho todo. */
  discountForAll: boolean;
}

/** Preco e saldo das variantes, conferidos pelo servidor (nunca pelo navegador). */
export interface PosCatalogFacts {
  unitCents: Readonly<Record<string, number>>;
  available: Readonly<Record<string, number | undefined>>;
}

export type PosPatchIntentLine =
  | { action: 'add'; productId: string; variantId: string; quantity: number }
  | { action: 'remove'; variantId: string }
  | { action: 'quantity'; variantId: string; quantity: number }
  | { action: 'discount'; variantId: string; discountBps: number };

export interface PosPatchIntentPayment {
  /** 1 a n; omitido = o primeiro. */
  position?: number;
  method?: PosPaymentMethod;
  installments?: number;
  amountCents?: number;
  tenderedCents?: number;
  cardMethodId?: string;
}

export interface PosPatchIntent {
  lines?: PosPatchIntentLine[];
  /** Mesmo desconto em todas as linhas. */
  discountAllBps?: number;
  /** `contactId` ja conferido na empresa pelo servidor; `none` = consumidor final. */
  customer?: { contactId: string } | { none: true };
  payments?: PosPatchIntentPayment[];
}

export type PosPatchRefusalCode =
  | 'empty_intent'
  | 'line_not_found'
  | 'line_duplicate'
  | 'too_many_ops'
  | 'quantity_invalid'
  | 'stock'
  | 'bulk_remove'
  | 'clear_all'
  | 'discount_none'
  | 'discount_limit'
  | 'discount_invalid'
  | 'customer_required'
  | 'credit_customer'
  | 'method_disabled'
  | 'installments_card_only'
  | 'installments_max'
  | 'installments_min'
  | 'split_disabled'
  | 'split_sum'
  | 'payment_position'
  | 'tendered_invalid'
  | 'price_unknown'
  | 'total_limit'
  | 'field_unknown';

export type PosPatchResult =
  | {
      ok: true;
      next: PosCartV1;
      ops: DraftPatchOp[];
      changes: DraftPatchChange[];
      totalCents: number;
    }
  | { ok: false; code: PosPatchRefusalCode; message: string; detail?: Record<string, unknown> };

const refuse = (
  code: PosPatchRefusalCode,
  message: string,
  detail?: Record<string, unknown>,
): PosPatchResult => ({ ok: false, code, message, ...(detail ? { detail } : {}) });

const MAX_LINES = 200;
const MAX_ADDED_LINES = 5;
const MAX_REMOVED_LINES = 2;
const MAX_ADDED_PAYMENTS = 4;
const MAX_OPS = 10;

/**
 * AV4: desconto no total que o rascunho declara (reais ou percentual), em centavos, sobre o subtotal já com os
 * descontos dos itens. Nunca passa do subtotal. O rascunho só informa; quem decide é a pessoa na tela.
 */
export function posTotalDiscountCents(
  cart: Pick<PosCartV1, 'discountTotalCents' | 'discountTotalBps'>,
  subtotalCents: number,
): number {
  const wanted =
    cart.discountTotalCents !== undefined
      ? cart.discountTotalCents
      : cart.discountTotalBps !== undefined
        ? totalDiscountFromBps(subtotalCents, cart.discountTotalBps)
        : 0;
  return Math.min(Math.max(0, wanted), Math.max(0, subtotalCents));
}

const percent = (bps: number): string => `${(bps / 100).toLocaleString('pt-BR')}%`;

function totalOf(cart: PosCartV1, facts: PosCatalogFacts): number | null {
  let total = 0;
  for (const line of cart.lines) {
    const unit = facts.unitCents[line.variantId];
    if (unit === undefined) return null;
    try {
      total += priceLine(unit, line.quantity, line.discountBps).totalCents;
    } catch {
      return null;
    }
  }
  if (!Number.isSafeInteger(total)) return null;
  return total - posTotalDiscountCents(cart, total);
}

/**
 * Transforma a INTENCAO em operacoes e em rascunho resultante, aplicando as
 * regras do dominio na mesma ordem do clique. Atomico: qualquer parte recusada
 * recusa o patch todo (a mensagem diz qual parte).
 */
export function buildPosPatch(
  current: PosCartV1,
  intent: PosPatchIntent,
  rules: PosPatchRules,
  facts: PosCatalogFacts,
): PosPatchResult {
  if (!intent.lines?.length && intent.discountAllBps === undefined && !intent.customer && !intent.payments?.length)
    return refuse('empty_intent', 'Não entendi o que preencher. Diga o que mudar na venda.');

  const ops: DraftPatchOp[] = [];
  let next = cloneJson(current);
  const apply = (op: DraftPatchOp): boolean => {
    const result = applyDraftPatch(next, [op]);
    if (!result.ok) return false;
    next = result.value as PosCartV1;
    ops.push(op);
    return true;
  };
  const indexOf = (variantId: string): number =>
    next.lines.findIndex((line) => line.variantId === variantId);
  const shortage = (variantId: string, quantity: number): string | null => {
    const available = facts.available[variantId];
    return available !== undefined && quantity > available
      ? `Só há ${available} em estoque neste local.`
      : null;
  };

  /* ---- itens ---- */
  let added = 0;
  let removed = 0;
  const touched = new Set<string>();
  for (const item of intent.lines ?? []) {
    const key = `${item.action}:${item.variantId}`;
    if (touched.has(key)) return refuse('line_duplicate', 'O mesmo item aparece duas vezes no pedido.');
    touched.add(key);
    const at = indexOf(item.variantId);
    if (item.action === 'add') {
      if (!Number.isInteger(item.quantity) || item.quantity < 1 || item.quantity > 100000)
        return refuse('quantity_invalid', 'A quantidade precisa ser de 1 a 100000.');
      if (at >= 0) {
        const quantity = next.lines[at]!.quantity + item.quantity;
        if (quantity > 100000) return refuse('quantity_invalid', 'A quantidade passa de 100000.');
        const low = shortage(item.variantId, quantity);
        if (low) return refuse('stock', low, { available: facts.available[item.variantId] });
        apply({ op: 'replace', path: `/lines/${at}/quantity`, value: quantity, expect: next.lines[at]!.quantity });
      } else {
        added += 1;
        if (added > MAX_ADDED_LINES || next.lines.length >= MAX_LINES)
          return refuse('too_many_ops', 'Posso incluir até 5 itens novos por vez.');
        const low = shortage(item.variantId, item.quantity);
        if (low) return refuse('stock', low, { available: facts.available[item.variantId] });
        apply({
          op: 'add',
          path: '/lines/-',
          value: {
            productId: item.productId,
            variantId: item.variantId,
            quantity: item.quantity,
            discountBps: 0,
          },
        });
      }
    } else if (at < 0) {
      return refuse('line_not_found', 'Não encontrei esse item na venda.');
    } else if (item.action === 'remove') {
      removed += 1;
      if (removed > MAX_REMOVED_LINES)
        return refuse('bulk_remove', 'Retirar 3 itens ou mais é com você na tela, para não perder a venda sem querer.');
      apply({ op: 'remove', path: `/lines/${at}`, expect: next.lines[at] });
    } else if (item.action === 'quantity') {
      if (!Number.isInteger(item.quantity) || item.quantity < 1 || item.quantity > 100000)
        return refuse('quantity_invalid', 'A quantidade precisa ser de 1 a 100000.');
      const low = shortage(item.variantId, item.quantity);
      if (low) return refuse('stock', low, { available: facts.available[item.variantId] });
      apply({ op: 'replace', path: `/lines/${at}/quantity`, value: item.quantity, expect: next.lines[at]!.quantity });
    } else {
      const wanted = item.discountBps;
      const verdict = checkDiscount(wanted, rules);
      if (verdict) return verdict;
      apply({ op: 'replace', path: `/lines/${at}/discountBps`, value: wanted, expect: next.lines[at]!.discountBps });
    }
  }
  if (!next.lines.length)
    return refuse('clear_all', 'Limpar o carrinho todo é com você, para não perder a venda sem querer.');

  if (intent.discountAllBps !== undefined) {
    const verdict = checkDiscount(intent.discountAllBps, rules);
    if (verdict) return verdict;
    next.lines.forEach((line, index) => {
      if (line.discountBps !== intent.discountAllBps)
        apply({
          op: 'replace',
          path: `/lines/${index}/discountBps`,
          value: intent.discountAllBps,
          expect: line.discountBps,
        });
    });
  }
  if (rules.discountForAll && new Set(next.lines.map((line) => line.discountBps)).size > 1)
    return refuse('discount_invalid', 'Aqui o desconto vale para o carrinho todo, o mesmo percentual em todos os itens.');

  /* ---- cliente ---- */
  if (intent.customer) {
    if ('none' in intent.customer) {
      if (rules.requireCustomer)
        return refuse('customer_required', 'Esta loja exige identificar o cliente. Quem é o cliente?');
      if (next.customerRef !== null)
        apply({ op: 'replace', path: '/customerRef', value: null, expect: next.customerRef });
    } else if (next.customerRef !== intent.customer.contactId) {
      if (next.payments.some((payment) => payment.method === 'credit'))
        return refuse('credit_customer', 'O pagamento está em crédito da loja do cliente atual. Troque a forma de pagamento antes de trocar o cliente.');
      apply({ op: 'replace', path: '/customerRef', value: intent.customer.contactId, expect: next.customerRef });
    }
  }

  /* ---- total (do servidor) ---- */
  const oldTotal = current.displayedTotalCents;
  const total = totalOf(next, facts);
  if (total === null) return refuse('price_unknown', 'Não consegui conferir o preço de um item. Tente de novo em instantes.');
  if (total > 1_000_000_000_000) return refuse('total_limit', 'O total passa do limite por venda.');

  /* ---- pagamentos ---- */
  const requested = intent.payments ?? [];
  if (requested.length) {
    const used = new Set<number>();
    const existing = next.payments.length;
    let created = 0;
    for (const item of requested) {
      const position = (item.position ?? 1) - 1;
      if (!Number.isInteger(position) || position < 0 || position > existing + created)
        return refuse('payment_position', 'Esse pagamento não existe na venda.');
      if (used.has(position)) return refuse('payment_position', 'O mesmo pagamento aparece duas vezes no pedido.');
      used.add(position);
      if (position >= next.payments.length) {
        created += 1;
        if (created > MAX_ADDED_PAYMENTS) return refuse('too_many_ops', 'Posso criar até 4 pagamentos por vez.');
        apply({
          op: 'add',
          path: '/payments/-',
          value: {
            method: 'cash',
            accountId: null,
            cardMethodId: null,
            amountCents: null,
            tenderedCents: null,
            installments: 1,
            received: false,
            creditId: null,
          } satisfies Payment,
        });
      }
    }
    if (next.payments.length > 1 && !rules.allowSplit)
      return refuse('split_disabled', 'Esta loja não permite dividir o pagamento.');

    // Valores: o Rimati NUNCA preenche nem troca dinheiro que a pessoa nao disse (AG2/AG3). O ponto de
    // partida e o que ja esta na tela; so o que a pessoa disser agora muda, e so campo vazio pode
    // receber valor. O unico jeito de preencher com o total sem fala e o botao "Usar o total".
    const amounts: Array<number | null> = next.payments.map((payment) => payment.amountCents);
    let saidNow = false;
    requested.forEach((item) => {
      const position = (item.position ?? 1) - 1;
      if (item.amountCents !== undefined) {
        amounts[position] = item.amountCents;
        saidNow = true;
      }
    });
    if (saidNow) {
      // A pessoa disse valor: o unico pagamento sem valor fecha a conta; a soma precisa bater.
      const fixed = amounts.reduce<number>((sum, value) => sum + (value ?? 0), 0);
      const open = amounts.flatMap((value, index) => (value === null ? [index] : []));
      if (open.length === 1) amounts[open[0]!] = total - fixed;
      const sum = amounts.reduce<number>((acc, value) => acc + (value ?? 0), 0);
      if (sum !== total || amounts.some((value) => value === null || value < 0))
        return refuse('split_sum', 'A soma dos pagamentos precisa ser igual ao total da venda.', {
          totalCents: total,
        });
    } else if (total !== oldTotal) {
      // Itens mudaram o total e a pessoa nao disse valor: como a tela, o pagamento unico que era
      // o total acompanha; varios pagamentos pedem que ela diga como dividir. Nada mais muda.
      if (amounts.length === 1 && amounts[0] !== null && amounts[0] === oldTotal) amounts[0] = total;
      else if (amounts.length > 1)
        return refuse('split_sum', 'O total mudou e há mais de um pagamento. Diga como dividir o novo total.', {
          totalCents: total,
        });
    }

    for (let index = 0; index < next.payments.length; index += 1) {
      const before = next.payments[index]!;
      const item = requested.find((entry) => (entry.position ?? 1) - 1 === index);
      const method = (item?.method ?? before.method) as Payment['method'];
      if (method !== 'credit' && !rules.allowedMethods.has(method as PosPaymentMethod))
        return refuse('method_disabled', `${METHOD_LABEL[method]} está desativado nas configurações desta loja.`);
      const installments =
        method === 'card' ? (item?.installments ?? (before.method === 'card' ? before.installments : 1)) : 1;
      if (item?.installments !== undefined && method !== 'card' && item.installments !== 1)
        return refuse('installments_card_only', 'Parcelamento é só no cartão.');
      if (!Number.isInteger(installments) || installments < 1 || installments > 60)
        return refuse('installments_max', `Esta loja aceita até ${rules.maxInstallments}x.`, {
          max: rules.maxInstallments,
        });
      if (method === 'card') {
        if (installments > rules.maxInstallments)
          return refuse('installments_max', `Esta loja aceita até ${rules.maxInstallments}x. Quer ${rules.maxInstallments}x?`, {
            max: rules.maxInstallments,
          });
        // Sem valor dito, a conferencia usa o total (so quando e um unico pagamento).
        const basis = amounts[index] ?? (next.payments.length === 1 ? total : null);
        const smallest = basis === null ? Number.POSITIVE_INFINITY : Math.floor(basis / installments);
        if (rules.minInstallmentCents > 0 && smallest < rules.minInstallmentCents) {
          const fit = Math.max(
            1,
            Math.min(rules.maxInstallments, Math.floor(basis! / rules.minInstallmentCents)),
          );
          return refuse(
            'installments_min',
            `Com ${installments}x cada parcela ficaria abaixo de ${money(rules.minInstallmentCents)}. O máximo é ${fit}x.`,
            { max: fit },
          );
        }
      }
      // Dinheiro entregue so entra quando a pessoa diz o valor. Sem fala dela, mantem o que a tela
      // ja tinha (se ainda cobre o pagamento) ou fica vazio, que na tela significa "valor exato".
      const keptTendered =
        before.method === 'cash' &&
        before.tenderedCents !== null &&
        before.tenderedCents >= (amounts[index] ?? 0)
          ? before.tenderedCents
          : null;
      const tendered = method === 'cash' ? (item?.tenderedCents ?? keptTendered) : null;
      if (item?.tenderedCents !== undefined && method !== 'cash')
        return refuse('tendered_invalid', 'Dinheiro entregue só vale para pagamento em dinheiro.');
      const owed = amounts[index] ?? (next.payments.length === 1 ? total : null);
      if (tendered !== null && owed !== null && tendered < owed)
        return refuse('tendered_invalid', 'O dinheiro entregue não pode ser menor que o valor a pagar.');
      const cardMethodId =
        method !== 'card' ? null : rules.operational ? before.cardMethodId : (item?.cardMethodId ?? before.cardMethodId);

      const set = <K extends keyof Payment>(key: K, value: Payment[K]): void => {
        if (canonicalJson(next.payments[index]![key]) !== canonicalJson(value))
          apply({
            op: 'replace',
            path: `/payments/${index}/${key}`,
            value,
            expect: next.payments[index]![key],
          });
      };
      const changedMeaning =
        method !== before.method || amounts[index] !== before.amountCents;
      set('method', method);
      set('amountCents', amounts[index] ?? null);
      set('installments', installments);
      set('tenderedCents', tendered);
      set('cardMethodId', cardMethodId);
      // Mudou a forma ou o valor: a confirmacao de recebimento deixa de valer.
      if (changedMeaning && next.payments[index]!.received) set('received', false);
    }
  } else if (total !== oldTotal) {
    // Itens mudaram o total: o pagamento unico acompanha, como a tela faria.
    if (next.payments.length === 1) {
      const only = next.payments[0]!;
      // Valor vazio continua vazio: o total so acompanha o que ja era o total.
      if (only.amountCents !== null && only.amountCents === oldTotal) {
        apply({ op: 'replace', path: '/payments/0/amountCents', value: total, expect: only.amountCents });
        if (only.method === 'cash' && only.tenderedCents !== null && only.tenderedCents < total)
          apply({ op: 'replace', path: '/payments/0/tenderedCents', value: total, expect: only.tenderedCents });
        if (only.received) apply({ op: 'replace', path: '/payments/0/received', value: false, expect: true });
      }
    }
    if (next.payments.length > 1)
      return refuse('split_sum', 'O total mudou e há mais de um pagamento. Diga como dividir o novo total.', {
        totalCents: total,
      });
  }
  if (next.displayedTotalCents !== total)
    apply({ op: 'replace', path: '/displayedTotalCents', value: total, expect: current.displayedTotalCents });

  if (ops.length > MAX_OPS + 30) return refuse('too_many_ops', 'O pedido tem alterações demais de uma vez.');
  const violation = posPatchViolation(ops);
  if (violation) return refuse('field_unknown', 'Esse campo eu não preencho por você.', { path: violation });
  const changes = diffPosCart(current, next);
  if (!changes.length)
    return refuse('empty_intent', 'A venda já está assim. Nada para preencher.');
  return { ok: true, next, ops, changes, totalCents: total };
}

function checkDiscount(bps: number, rules: PosPatchRules): PosPatchResult | null {
  if (!Number.isInteger(bps) || bps < 0 || bps > 10000)
    return refuse('discount_invalid', 'Desconto inválido.');
  if (bps > rules.discountLimitBps) {
    return rules.discountLimitBps === 0
      ? refuse('discount_none', 'Seu perfil não dá desconto aqui.', { limitBps: 0 })
      : refuse(
          'discount_limit',
          `Seu limite de desconto é ${percent(rules.discountLimitBps)}. Posso colocar ${percent(rules.discountLimitBps)} agora, ou pedir autorização para ${percent(bps)}.`,
          { limitBps: rules.discountLimitBps, requestedBps: bps },
        );
  }
  return null;
}

/**
 * Revalida o rascunho RESULTANTE contra as regras (usada pela pagina antes de
 * aplicar e pelo servidor sobre o que recebeu): alcada e estrutura, sem preco.
 */
export function validatePosCartAgainstRules(
  cart: PosCartV1,
  rules: PosPatchRules,
): { ok: true } | { ok: false; field: string; message: string } {
  for (const [index, line] of cart.lines.entries())
    if (line.discountBps > rules.discountLimitBps)
      return { ok: false, field: `lines.${line.variantId}.discountBps`, message: `Desconto acima do limite no item ${index + 1}.` };
  for (const [index, payment] of cart.payments.entries()) {
    if (payment.method === 'card' && payment.installments > rules.maxInstallments)
      return { ok: false, field: `payments.${index}.installments`, message: 'Parcelas acima do limite da loja.' };
    if (payment.method !== 'card' && payment.installments !== 1)
      return { ok: false, field: `payments.${index}.installments`, message: 'Parcelamento é só no cartão.' };
  }
  if (cart.payments.length > 1 && !rules.allowSplit)
    return { ok: false, field: 'payments', message: 'Dividir pagamentos está desativado.' };
  return { ok: true };
}
